import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import './LoginPage.css';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLocalLogin, setShowLocalLogin] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');

  const { login, googleLogin, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for a session-expiry or sign-out message set by the auth interceptor
  useEffect(() => {
    const message = localStorage.getItem('panorama_login_message');
    if (message) {
      setInfoMessage(message);
      localStorage.removeItem('panorama_login_message');
    }
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      setError('Please enter both username and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await login(formData.username, formData.password);
      // Navigation will be handled by the useEffect above
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError('');

    try {
      await googleLogin(credentialResponse.credential);
      // Navigation will be handled by the useEffect above
    } catch (err) {
      setError(err.message || 'Google login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google login failed. Please try again.');
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <img 
            src="/encore_logo_notext_min.png" 
            alt="Panorama" 
            className="login-logo"
          />
          <h1>Welcome to Panorama</h1>
          <p>Please sign in to continue</p>
        </div>

        <div className="login-content">
          {infoMessage && (
            <div className="info-message">
              <span className="info-icon">🔒</span>
              {infoMessage}
            </div>
          )}

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {/* Google Login Section */}
          <div className="google-login-section">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme="outline"
              size="large"
              text="signin_with"
              shape="rectangular"
              logo_alignment="left"
              width="100%"
            />
          </div>

          <div className="login-divider">
            <span>or</span>
          </div>

          {/* Local Login Toggle */}
          {!showLocalLogin ? (
            <button 
              type="button"
              className="admin-login-toggle"
              onClick={() => setShowLocalLogin(true)}
              disabled={loading}
            >
              Local User Login
            </button>
          ) : (
            <div className="local-login-section">
              <form onSubmit={handleLocalLogin} className="login-form">
                <div className="form-group">
                  <label htmlFor="username">Username</label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="Enter your username"
                    disabled={loading}
                    autoComplete="username"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    disabled={loading}
                    autoComplete="current-password"
                    required
                  />
                </div>

                <button 
                  type="submit" 
                  className="login-button"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="loading-spinner">
                      <span className="spinner"></span>
                      Signing in...
                    </span>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>

              <button 
                type="button"
                className="back-button"
                onClick={() => {
                  setShowLocalLogin(false);
                  setFormData({ username: '', password: '' });
                  setError('');
                }}
                disabled={loading}
              >
                ← Back to Google Login
              </button>
            </div>
          )}
        </div>

        <div className="login-footer">
          <p>
            For access contact Encore Interactive support.
          </p>
          <p style={{ marginTop: '10px' }}>
            <Link to="/terms" style={{ color: '#667eea', textDecoration: 'none' }}>
              Terms and Conditions
            </Link>
            {' | '}
            <Link to="/privacy" style={{ color: '#667eea', textDecoration: 'none' }}>
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
